import { Anchor, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const footerLinks = {
    products: [
      { label: 'Custom T-Shirts', href: '#products' },
      { label: 'Tote Bags', href: '#products' },
      { label: 'Posters & Wall Art', href: '#products' },
      { label: 'Hats & Caps', href: '#products' },
    ],
    company: [
      { label: 'About Us', href: '#' },
      { label: 'How it Works', href: '#how-it-works' },
      { label: 'Integrations', href: '#integrations' },
      { label: 'Contact', href: '#contact' },
    ],
    support: [
      { label: 'FAQ', href: '#faq' },
      { label: 'Shipping Info', href: '#' },
      { label: 'Returns Policy', href: '#' },
      { label: 'Privacy Policy', href: '#' },
    ],
  };

  const scrollToSection = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer
      data-testid="footer"
      className="bg-coastal-navy text-white"
    >
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center">
            <h2
              data-testid="footer-cta-heading"
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Ready to{' '}
              <span className="text-coastal-orange">build your brand?</span>
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Join hundreds of brands who trust Coastal Route Commerce to bring
              their products to life with premium quality and fast fulfillment.
            </p>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              data-testid="footer-cta-btn"
              className="inline-flex items-center gap-2 bg-coastal-orange hover:bg-coastal-orange/90 text-white rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
            >
              Start Your Journey
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <a
              href="/"
              data-testid="footer-logo"
              className="flex items-center gap-2 mb-6"
            >
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Anchor className="w-6 h-6 text-coastal-navy" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-white text-lg leading-tight">
                  Coastal Route
                </span>
                <span className="text-xs text-white/50 font-medium tracking-wide">
                  COMMERCE
                </span>
              </div>
            </a>

            <p className="text-white/60 mb-6 max-w-sm">
              Premium print-on-demand fulfillment services. Design, create, and
              sell custom products with no upfront costs or inventory.
            </p>

            {/* Contact Info */}
            <div
              data-testid="footer-contact"
              className="space-y-3"
            >
              <a
                href="mailto:admin@CoastalRouteCommerce.shop"
                className="flex items-center gap-3 text-white/60 hover:text-coastal-orange transition-colors"
              >
                <Mail className="w-5 h-5" />
                admin@CoastalRouteCommerce.shop
              </a>
              <a
                href="tel:+15154438615"
                className="flex items-center gap-3 text-white/60 hover:text-coastal-orange transition-colors"
              >
                <Phone className="w-5 h-5" />
                +1 515 443 8615
              </a>
              <p className="flex items-center gap-3 text-white/60">
                <MapPin className="w-5 h-5" />
                2015 E 43rd St, Tacoma WA 98404
              </p>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">
              Products
            </h3>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/60 hover:text-coastal-orange transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/60 hover:text-coastal-orange transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">
              Support
            </h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/60 hover:text-coastal-orange transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              data-testid="footer-copyright"
              className="text-white/50 text-sm"
            >
              © {new Date().getFullYear()} Coastal Route Commerce LLC. All
              rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="#"
                className="text-white/50 hover:text-white transition-colors text-sm"
              >
                Terms of Service
              </a>
              <a
                href="#"
                className="text-white/50 hover:text-white transition-colors text-sm"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
